import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import { ArrowDown, ArrowUp, CornerDownLeft, Package, Search, Store, User } from 'lucide-react';

import { useBranches, useProducts, useUsers } from '../../hooks';
import { canSeePage, getNavSections } from '../../navigation';
import { useI18n } from '../../i18n';
import { createLink } from '../../utils';
import { ROUTE_URLS } from '../../routes';

const MAX_PER_GROUP = 6;

const matches = (text, query) => text?.toString().toLowerCase().includes(query);

export const CommandPalette = ({ open, onClose }) => {
  const { isAdmin, pages: allowedPages } = useSelector(state => state.authReducer);
  const { t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  const { data: products } = useProducts({ enabled: open });
  const { data: branches } = useBranches({ enabled: open });
  const { data: users } = useUsers({ enabled: open && isAdmin });

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const canSeeBranches = canSeePage(isAdmin, allowedPages, 'branches');
    const pages = getNavSections(isAdmin, allowedPages)
      .flatMap(section => section.items)
      .map(item => ({ id: `page-${item.key}`, label: t(`nav.${item.key}`), icon: item.icon, to: item.path }))
      .filter(item => !q || matches(item.label, q));

    const result = [{ key: 'pages', items: pages }];
    if (q) {
      if (isAdmin) {
        result.push({
          key: 'products',
          items: (Array.isArray(products) ? products : [])
            .filter(p => matches(p.name, q))
            .slice(0, MAX_PER_GROUP)
            .map(p => ({
              id: `product-${p.id}`,
              label: p.name,
              icon: Package,
              to: createLink(ROUTE_URLS.EDIT_PRODUCT, p.id),
            })),
        });
      }
      result.push({
        key: 'branches',
        items: (canSeeBranches && Array.isArray(branches) ? branches : [])
          .filter(b => matches(b.name, q))
          .slice(0, MAX_PER_GROUP)
          .map(b => ({
            id: `branch-${b.id}`,
            label: b.name,
            icon: Store,
            to: createLink(isAdmin ? ROUTE_URLS.EDIT_BRANCH : ROUTE_URLS.BRANCH_PRICES, b.id),
          })),
      });
      if (isAdmin) {
        result.push({
          key: 'users',
          items: (Array.isArray(users) ? users : [])
            .filter(u => matches(u.login, q))
            .slice(0, MAX_PER_GROUP)
            .map(u => ({
              id: `user-${u.id}`,
              label: u.login,
              icon: User,
              to: createLink(ROUTE_URLS.EDIT_USER, u.id),
            })),
        });
      }
    }
    return result.filter(group => group.items.length > 0);
  }, [query, isAdmin, allowedPages, products, branches, users, t]);

  const flat = useMemo(() => groups.flatMap(group => group.items), [groups]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    listRef.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const go = item => {
    if (!item) return;
    onClose();
    navigate(item.to);
  };

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[activeIndex]);
    }
  };

  let cursor = -1;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={600}
      className='wm-command'
      style={{ top: '12vh' }}
      destroyOnClose
    >
      <div className='wm-command__input'>
        <Search size={18} strokeWidth={1.75} />
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('search.placeholder')}
          aria-label={t('common.search')}
          role='combobox'
          aria-expanded='true'
          aria-controls='wm-command-list'
          aria-activedescendant={flat[activeIndex]?.id}
        />
        <kbd>Esc</kbd>
      </div>
      <div className='wm-command__list' ref={listRef} id='wm-command-list' role='listbox'>
        {flat.length === 0 ? (
          <div className='wm-empty wm-empty--compact'>
            <div className='wm-empty__text'>{t('search.empty')}</div>
          </div>
        ) : (
          groups.map(group => (
            <div key={group.key} role='group' aria-label={t(`search.${group.key}`)}>
              <div className='wm-command__group'>{t(`search.${group.key}`)}</div>
              {group.items.map(item => {
                cursor += 1;
                const index = cursor;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    id={item.id}
                    type='button'
                    role='option'
                    aria-selected={index === activeIndex}
                    className={`wm-command__item${index === activeIndex ? ' is-active' : ''}`}
                    onMouseMove={() => setActiveIndex(index)}
                    onClick={() => go(item)}
                  >
                    <Icon size={16} strokeWidth={1.75} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
      <div className='wm-command__footer'>
        <span>
          <kbd>
            <ArrowUp size={11} />
          </kbd>
          <kbd>
            <ArrowDown size={11} />
          </kbd>
          {t('search.hintNavigate')}
        </span>
        <span>
          <kbd>
            <CornerDownLeft size={11} />
          </kbd>
          {t('search.hintOpen')}
        </span>
        <span>
          <kbd>Esc</kbd>
          {t('search.hintClose')}
        </span>
      </div>
    </Modal>
  );
};
